import { Subject, Subscription } from "rxjs";

// Cerium
import { FieldGroupMetadata, FieldMetadata, ViewMetadata, ViewMetadataUtil } from "@data/cer-data-types";

import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from "@ui/cer-ui-types";

import { CerRandom } from "src/cer/cer-util/cer-random";

import { CerFormRouteOptions } from "@form/cer-form-route-options";
import { CerFormPaneVisible, CerFormPaneType } from "@form/cer-form-panes/cer-form-panes.service";
import { CerGridEditService } from "../cer-grid/cer-grid-edit.service";

import { CerViewDocumentType } from "@form/cer-form-view-type/cer-form-view-type-document/cer-form-view-document-type";

import { CerAutoFocus, CerGridEditSettings, CerGridPaging, CerGridSelectionMode, CerGridToolbarType } from "src/cer/cer-grid/cer-grid.component";
import { CerFormViewDataLinksSetup } from "./cer-form-view-data-link";

export type CerViewType = 'list' | 'card' | 'tree' | 'document' | 'custom';
export type CerViewRole = 'view' | 'virtual';  // "Virtual" view is never presented in the UI

export declare interface ICerViewComponent {
  id: string;
  formView: CerFormView;
  viewMetadata: ViewMetadata;
  fieldMetadata: FieldMetadata[];
  dataParent: boolean;
  dataChild: boolean;
  dataChildOfIds: string[];
  editService: CerGridEditService;

  isEdit(): boolean;
  parentRowData(): any; // Get the parant row
  rowSelectByIdx(idx: number): void; // Set selection by row index
  rowSelectedData(): any; // Get selected row data
  rowSelectedRefresh(): void; // Re-query selected row
  rowsSelectedId(): number[]; // Get selected position in rows
  rowsSelectedData(): any[]; // Get all selected rows data
  rowSelectedKeep(): void; // Save position in rows and reselect row, when UI is reset

  research(): void; // Re-query data
  reflow(): void; // Adjust UI and redraw based on viewport/size change in DOM

  onViewCommand(event: UiCommandEvent): void;
}

export interface ICerFormView {
  title?: string;  
  titleHeaderVisible?: boolean;
  paneTarget?: CerFormPaneType;
  mandatory?: boolean,
  autoFocus?: CerAutoFocus;
  viewType?: CerViewType;
  dataChild?: boolean;
  viewMetadata?: ViewMetadata;
  fieldMetadata?: FieldMetadata[];
  fieldGroupMetadata?: FieldGroupMetadata[];
  toolbarCommands?: UiCommand[];
  contextMenuCommands?: UiCommand[];
  keyboardShortcuts?: UiKeyboardShortcut[];
  onCommandEvent?: ((e: UiCommandEvent) => void);
  // List
  listSelectionMode?: CerGridSelectionMode;
  // Document view - options
  document?: string;
  documentUrl?: string;
  documentType?: CerViewDocumentType;

  dataLink?: CerFormViewDataLinksSetup;

  state? : Map<string, any>;
}

export class CerFormView implements ICerFormView {
  dataLinksSetup() {
    throw new Error("Method not implemented.");
  }
  constructor(options: ICerFormView = null) {
    if (options) {
      for (var property in options) {
        if (options.hasOwnProperty(property))
          (this as any)[property] = (options as any)[property];
      }
    }
  }

  public id: string;
  public paneTarget?: CerFormPaneType;
  public title?: string;
  public titleHeaderVisible: boolean = false;
  public visibleDefault: CerFormPaneVisible = 'show';
  public mandatory: boolean = false;

  // Route information
  public routeOptions: CerFormRouteOptions;

  // View type
  public viewType: CerViewType = 'list';
  public role: CerViewRole = 'view';
  public autoFocus?: CerAutoFocus = 'default';

  // List/card view type - adapter
  public parentView: CerFormView; // Master/detail: Master where current view is detail
  public dataParent: boolean; // Master/detail: Master where current view is master
  public dataChild: boolean; // Master/detail: Master where current view is detail
  public dataChildOfIds: string[]; // Master/detail: Id of master view
  public dataChildrenExists: boolean;
  public dataFromParent?: any[];

  // List/card view type
  public viewMetadata?: ViewMetadata;
  public fieldMetadata?: FieldMetadata[] = [];
  public fieldGroupMetadata?: FieldGroupMetadata[] = [];

  // List/card custom view type
  public appComponent: ICerViewComponent; // Custom object;

  // List/card view - commands
  public toolbarType: CerGridToolbarType = 'default';
  public toolbarCommands?: UiCommand[];
  public contextMenuCommands?: UiCommand[];
  public keyboardShortcuts?: UiKeyboardShortcut[];

  // List view - options
  public listSelectionMode: CerGridSelectionMode = 'row';
  public listPaging: CerGridPaging = 'default';
  public listEditSettings: CerGridEditSettings;

  // Document view - options
  public document?: string;
  public documentFileName?: string;
  public documentUrl?: string;
  public documentUrlUpdate?: string;
  public documentType?: CerViewDocumentType;
  public documentOriginalFileName?: string;
  public documentOriginalUrl?: string;

  // Documentation / help topic
  public documentationTopic?: string;

  // DataLinks 
  public dataLink?: CerFormViewDataLinksSetup;
  
  // State
  public state : Map<string, any> = new Map<string, any>();

  private randomId: string = CerRandom.AsString(10);
  public getId(): string {
    var id: string[] = [this.viewType];
    var vm = this.viewMetadata;
    id.push(vm?.dataApiName ?? vm?.dataApiName ?? this.title ?? 'view');
    id.push(this.randomId);
    return id.join('_').replaceAll(' ','_').replaceAll('-','_').replace('/[^a-zA-Z0-9_]/g', '');
  }

  private viewMetadataNameGet(viewMeta: ViewMetadata): string {
    if (viewMeta.dataApiName?.length > 0) {
      return viewMeta.dataApiName;
    }
    else {
      return 'view_' + this.randomId;
    }
  }

  public prepare() {
    if (this.viewMetadata && this.viewMetadata.name == null) {
      this.viewMetadata.name = this.viewMetadataNameGet(this.viewMetadata);
    }
    this.id ??= this.getId();
    this.viewMetadata
    this.dataParent ??= true;
    if (this.parentView) {
      if (this.parentView.dataParent == true) {
        if (this.viewMetadata == null || ViewMetadataUtil.linkIsParentLinkDefined(this.viewMetadata)) {
          this.dataChild = true;
          if (this.dataChildOfIds == null)  {
            this.dataChildOfIds = [this.parentView.id];
          }
          this.parentView.dataChildrenExists = true;
        }
      }
    }
    else {
      this.mandatory = true;
    }
  }

  public moveToPane(activePaneType: CerFormPaneType): void {
    if (activePaneType != this.paneTarget) {
      this.paneTarget = activePaneType;
      if (this.paneTarget == 'bottom' && this.listPaging == 'card') {
        this.listPaging = 'default';
      }
    }
  }

  static formViewByUiEvent(event: UiCommandEvent) {
    if (event.formView != null) {
      return event.formView;
    }
    return this.formViewByComponent(event.appComponent);
  }

  static formViewByComponent(appComponent: ICerViewComponent): CerFormView {
    if (appComponent == null) {
      return null;
    }
    if (appComponent.formView == null) {
      const view: CerFormView = new CerFormView();
      view.id = appComponent.id;
      view.role = 'virtual';
      view.paneTarget = null;
      view.viewType = null;
      view.viewMetadata = appComponent.viewMetadata;
      view.fieldMetadata = appComponent.fieldMetadata;
      view.dataParent = appComponent.dataParent;
      view.dataChild = appComponent.dataChild;
      view.dataChildOfIds = appComponent.dataChildOfIds;
      view.appComponent = appComponent;
      appComponent.formView = view;
    }
    return appComponent.formView;
  }

  // Events
  public onCommandEvent: ((e: UiCommandEvent) => void);  // All events from view is emitted here for simple consumption
  public triggerCommand(e: UiCommandEvent) {
    if (this.onCommandEvent != null) {
      this.onCommandEvent(e);
    }
  }

  public research() {
    const e: UiCommandEvent = { source: UiCommandSourceEnum.Event, args: null, commandId: null, actionType: UiActionTypeEnum.Research, subAction: null };
    this.commandNext(e);
  }

  public commandUpdateTitle(title: string) {
    this.commandNext({ 
      source: UiCommandSourceEnum.DesignChange, actionType: UiActionTypeEnum.TitleUpdate, 
      formView: this, subAction: null, commandId: null, args: null,
      rowData: { title: title} });
  }
  
  public commandParentData(rowData: any) {
    this.commandNext( {
      source: UiCommandSourceEnum.ActionComplete, actionType: UiActionTypeEnum.RefreshFromParent,
      formView: this, subAction: null, commandId: null, args: null,
      rowData: rowData });
  }

  // Internal subject to publish 
  private command$: Subject<UiCommandEvent> = new Subject<UiCommandEvent>(); // Component will next to subject using "commandTrigger".

  public commandNext(e: UiCommandEvent) {
    e.formView = this;
    this.command$?.next(e);
  }

  public commandSubscribe(onCommandHandler: (e: UiCommandEvent) => void): Subscription {
    return this.command$.subscribe(e => onCommandHandler(e));
  }
}

